import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import startOfDay from "date-fns/startOfDay";

import {
  AutoComplete,
  Button,
  Checkbox,
  Loader,
  SelectInput,
  TextInput,
  DatePicker,
} from "fe-shared/src/components";
import {
  CalendarIcon,
  PriorityHighIcon,
  PriorityLowIcon,
  PriorityMidIcon,
} from "fe-shared/src/components/Icons";
import useValidation from "fe-shared/src/hooks/useValidation";
import {
  getDateObject,
  getDateString,
  getNoOffsetDate,
} from "fe-shared/src/helpers/dates";

import {
  ParticipantOption,
  PickupRequestFormValues,
  PickupRequestPriorityId,
} from "../../types";
import { fromPickupRequestPriorityId } from "../../helpers/pickupRequestStatus";
import {
  formatWeight,
  getTotalQuantity,
  getTotalWeight,
} from "../../helpers/materials";

import ParticipantSelect from "../ParticipantSelect";

import {
  MAX_COMMENT_LENGTH,
  MAX_PRIORITY_REASON_LENGTH,
} from "./modules/config";
import {
  getPickupRequestFormValidationConfig,
  pickupRequestFormErrorMessages,
} from "./modules/validation";

import styles from "./PickupRequestForm.module.scss";

// SelectInput has no clear affordance, so the way back to "nothing selected" is an option whose
// value is the empty string - which the control already renders as the placeholder.
const CLEAR_SELECTION = "";

export interface HaulerOption {
  id: number;
  name: string;
  registrationNumber?: string;
  // Holds the hauler's eTracks regNumber as a string (see getPickupRequestHaulers), not the RPRA number.
  divisionId: number;
  tags?: string[];
}

export interface CarrierOption {
  id: number;
  name: string;
}

export interface MaterialTypeOption {
  code: string;
  name: string;
  standardWeight?: number;
}

export interface Props {
  collectorOptions: ParticipantOption[];
  collectorSearchText: string;
  onCollectorSearchTextChange: (text: string) => void;
  haulerOptions: HaulerOption[];
  /** The collector `haulerOptions` were loaded for. The parent learns of a collector change one render
   *  after the form does, so until it catches up the options still belong to the previous collector. */
  haulerOptionsCollectorId?: number | null;
  carrierOptions: CarrierOption[];
  materialTypes: MaterialTypeOption[];
  isCollectorsLoading?: boolean;
  isMaterialsLoading?: boolean;
  value: PickupRequestFormValues;
  onChange: (next: PickupRequestFormValues) => void;
  onSubmit: (values: PickupRequestFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  /** Blocks submit for a required field the surface owns via a slot, e.g. the "Other" reason. */
  isSubmitBlocked?: boolean;
  serverErrors?: Record<string, string>;
  /** Request reason control — owned by the surface, since a collector may not set it. */
  reasonsSlot?: React.ReactNode;
  /** Supporting documents uploader — owned by the surface that holds the upload API. */
  documentsSlot?: React.ReactNode;
  /** Edit mode: the collector is fixed, rendered as a read-only row and not validated. */
  isCollectorLocked?: boolean;
  /** Renders the locked collector as the disabled select instead of a plain row, so a surface
   *  that never had a choice still shows the field it would have chosen from. */
  lockedCollectorAsSelect?: boolean;
  submitLabel?: string;
  /** Hides the portal-only hauler, carrier and priority controls. Hauler and carrier then submit
   *  as null; priority still submits its Low default, which the create endpoint counts as unset. */
  showPortalOnlyFields?: boolean;
  /** Edit mode: the stored priority can no longer change, so the select is shown but disabled. */
  isPriorityLocked?: boolean;
  /** Edit mode: hauler and carrier are changed through their own actions, so the controls are
   *  hidden and the seeded ids go out unchanged. */
  isAssignmentLocked?: boolean;
}

const NO_SERVER_ERRORS: Record<string, string> = {};

const PRIORITY_ICONS = {
  [PickupRequestPriorityId.Low]: <PriorityLowIcon />,
  [PickupRequestPriorityId.Mid]: <PriorityMidIcon />,
  [PickupRequestPriorityId.High]: <PriorityHighIcon />,
};

const PRIORITY_IDS = [
  PickupRequestPriorityId.Low,
  PickupRequestPriorityId.Mid,
  PickupRequestPriorityId.High,
];

const PickupRequestForm = (props: Props) => {
  const {
    collectorOptions,
    onCollectorSearchTextChange,
    haulerOptions,
    haulerOptionsCollectorId,
    carrierOptions,
    materialTypes,
    isCollectorsLoading = false,
    isMaterialsLoading = false,
    value,
    onChange,
    onSubmit,
    onCancel,
    isSubmitting = false,
    isSubmitBlocked = false,
    serverErrors = NO_SERVER_ERRORS,
    reasonsSlot,
    documentsSlot,
    isCollectorLocked = false,
    lockedCollectorAsSelect = false,
    submitLabel,
    showPortalOnlyFields = true,
    isPriorityLocked = false,
    isAssignmentLocked = false,
  } = props;
  const showAssignment = showPortalOnlyFields && !isAssignmentLocked;

  const { t } = useTranslation();

  // Seeds useValidation's internal state once on mount only — see useValidation's own
  // useState(() => ...) lazy initializer, which never re-reads this value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialContent: PickupRequestFormValues = useMemo(() => value, []);

  const earliestPickupDate = useMemo(() => {
    const saved = getDateObject(initialContent.pickupDateFrom);
    const today = getNoOffsetDate(startOfDay(new Date())) as Date;

    return saved && saved.getTime() < today.getTime() ? saved : today;
  }, [initialContent.pickupDateFrom]);

  const validationConfig = useMemo(
    () => getPickupRequestFormValidationConfig(isCollectorLocked, earliestPickupDate),
    [isCollectorLocked, earliestPickupDate]
  );

  const {
    errors,
    values,
    isValid,
    updateValue,
    validateValue,
    addArrayValue,
    removeArrayValue,
  } = useValidation(
    validationConfig,
    initialContent,
    pickupRequestFormErrorMessages
  );

  useEffect(() => {
    onChange(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const preselectedHaulerCollectorId = useRef<number | null | undefined>(
    undefined
  );

  // Re-running for the same collector would restore the auto-assigned hauler over the user's pick.
  // Clearing the field drops the guard: handleCollectorChange wipes the hauler, so picking the
  // same collector again has to assign it afresh. Skipped when the collector is locked: it exists
  // to default a hauler right after a collector is *picked*, and a locked collector already
  // carries its real hauler on mount - the default would silently overwrite it. Skipped just as
  // much with the hauler field hidden, where the request is meant to leave unassigned.
  useEffect(() => {
    if (isCollectorLocked || !showPortalOnlyFields) return;
    if (!values.collectorId) {
      preselectedHaulerCollectorId.current = undefined;
      return;
    }
    if (!haulerOptions.length) return;
    if (
      haulerOptionsCollectorId !== undefined &&
      haulerOptionsCollectorId !== values.collectorId
    )
      return;
    if (preselectedHaulerCollectorId.current === values.collectorId) return;

    preselectedHaulerCollectorId.current = values.collectorId;

    const lastHauler = haulerOptions.find((hauler) => hauler.divisionId === 1);
    if (lastHauler) updateValue("haulerId", lastHauler.id, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.collectorId,
    haulerOptions,
    haulerOptionsCollectorId,
    isCollectorLocked,
    showPortalOnlyFields,
  ]);

  // A server error describes the value that was submitted; editing that field makes it stale.
  // The snapshot is retaken whenever a new result arrives, so a re-submit shows it again.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const submittedValues = useMemo(() => values, [serverErrors]);

  const serverError = (key: string) =>
    JSON.stringify(values[key as keyof PickupRequestFormValues]) ===
    JSON.stringify(submittedValues[key as keyof PickupRequestFormValues])
      ? serverErrors[key]
      : undefined;

  const fieldError = (key: string) => errors[key] || serverError(key);

  // Offered only once something is selected: it is a way back to empty, not a real choice.
  // SelectInput renders the placeholder for any falsy value, so a cleared field shows the
  // placeholder again rather than this label.
  const clearOption = (isSelected: boolean) =>
    isSelected
      ? [{ value: CLEAR_SELECTION, label: t("pickupRequests.form.notSelected") }]
      : [];

  const haulerAutoCompleteOptions = useMemo(
    () =>
      haulerOptions.map((hauler) => ({
        value: hauler.id,
        label: hauler.registrationNumber
          ? `${hauler.name} (${hauler.registrationNumber})`
          : hauler.name,
        description:
          t(`pickupRequests.haulerDivisions.${hauler.divisionId}`, {
            defaultValue: "",
          }) || undefined,
      })),
    [haulerOptions, t]
  );

  const selectedHauler = haulerAutoCompleteOptions.find(
    (option) => option.value === values.haulerId
  );

  // AutoComplete seeds its text with the selected label, so the plain label filter would
  // narrow the list to that one hauler on open; the untouched label shows every option.
  const filterHaulerOption = (
    option: { label: string },
    query: string
  ) =>
    query === selectedHauler?.label.toLowerCase() ||
    option.label.toLowerCase().includes(query);

  const carrierSelectOptions = useMemo(
    () => [
      ...clearOption(!!values.carrierId),
      ...carrierOptions.map((carrier) => ({
        value: carrier.id,
        label: carrier.name,
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carrierOptions, values.carrierId, t]
  );

  const priorityOptions = useMemo(
    () =>
      PRIORITY_IDS.map((id) => ({
        value: id,
        label: t(
          `pickupRequests.priorities.${fromPickupRequestPriorityId(id)}`
        ),
        labelIcon: PRIORITY_ICONS[id],
      })),
    [t]
  );

  const standardWeightByCode = useMemo(
    () =>
      materialTypes.reduce<Record<string, number>>((result, materialType) => {
        result[materialType.code] = materialType.standardWeight ?? 0;
        return result;
      }, {}),
    [materialTypes]
  );

  const totalQuantity = getTotalQuantity(values.materials);
  const totalWeight = getTotalWeight(
    values.materials.map(({ materialCode, quantity }) => ({
      code: materialCode,
      quantity,
    })),
    standardWeightByCode
  );

  const isHaulerBlockedByOrder = !values.collectorId;
  const isCarrierBlockedByOrder = !values.haulerId;

  // Clears run unvalidated first so the validated call last sees the post-clear values —
  // useValidation's isValid comes from whichever call had isValidated=true, so validating
  // before the clear would check the stale (pre-clear) hauler/carrier against the schema.
  const handleCollectorChange = (option: ParticipantOption | null) => {
    updateValue("haulerId", null);
    updateValue("carrierId", null);
    updateValue("collectorId", option ? option.companyId : null, true);
  };

  const handleHaulerChange = (haulerId: number | string) => {
    // Clearing the hauler clears the carrier with it: a carrier belongs to one hauler.
    updateValue("carrierId", null);
    updateValue("haulerId", haulerId === CLEAR_SELECTION ? null : (haulerId as number), true);
  };

  const handleMaterialToggle = (materialCode: string, isChecked: boolean) => {
    const index = values.materials.findIndex(
      (material) => material.materialCode === materialCode
    );

    if (isChecked && index === -1) {
      addArrayValue("materials", { materialCode, quantity: null });
      return;
    }

    if (!isChecked && index !== -1) {
      removeArrayValue("materials", index);
    }
  };

  const isSubmitDisabled = isSubmitting || !isValid || isSubmitBlocked;

  const handleSubmit = () => {
    if (isSubmitDisabled) {
      return;
    }

    onSubmit(values);
  };

  const commentValue = values.comment ?? "";
  const priorityReasonValue = values.priorityReason ?? "";

  const lockedCollectorName = collectorOptions.find(
    (option) => option.companyId === values.collectorId
  )?.displayName;

  const lockedCollectorField = lockedCollectorAsSelect ? (
    <ParticipantSelect
      disabled
      id="pickup-request-collector"
      options={collectorOptions}
      value={values.collectorId}
      onSearchTextChange={onCollectorSearchTextChange}
      onChange={handleCollectorChange}
      title={t("pickupRequests.form.collectorLabel")}
      className={styles.formField}
    />
  ) : (
    <div className={styles.readOnlyField}>
      <span className={styles.readOnlyLabel}>
        {t("pickupRequests.form.collectorLabel")}
      </span>
      <span>{lockedCollectorName}</span>
    </div>
  );

  return (
    <div className={styles.formWrapper}>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {t("pickupRequests.form.collectorSectionTitle")}
        </h2>
        {isCollectorLocked ? (
          lockedCollectorField
        ) : (
          <ParticipantSelect
            id="pickup-request-collector"
            required
            options={collectorOptions}
            value={values.collectorId}
            onSearchTextChange={onCollectorSearchTextChange}
            onChange={handleCollectorChange}
            onBlur={() => validateValue("collectorId")}
            isLoading={isCollectorsLoading}
            disabled={isSubmitting || isCollectorsLoading}
            title={t("pickupRequests.form.collectorLabel")}
            placeholder={t("pickupRequests.form.collectorPlaceholder")}
            isError={!!fieldError("collectorId")}
            errorText={fieldError("collectorId")}
            className={styles.formField}
          />
        )}
        {reasonsSlot}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          {t("pickupRequests.form.pickupDateSectionTitle")}
        </h2>
        <DatePicker
          isNoOffset
          id="pickup-request-date-range"
          title={t("pickupRequests.form.pickupDateLabel")}
          required
          placeholder=""
          disabled={isSubmitting}
          isError={
            !!fieldError("pickupDateFrom") || !!fieldError("pickupDateTo")
          }
          errorText={fieldError("pickupDateFrom") || fieldError("pickupDateTo")}
          minDate={new Date()}
          inputIconContent={<CalendarIcon />}
          startDate={getDateObject(values.pickupDateFrom)}
          setStartDate={(date) => {
            updateValue("pickupDateFrom", getDateString(date), true);
            updateValue("pickupDateTo", null);
          }}
        />
      </section>

      <section className={styles.section}>
        <h2 className={`${styles.sectionTitle} ${styles.sectionTitleRequired}`}>
          {t("pickupRequests.form.materialsSectionTitle")}
        </h2>
        <div className={styles.materialsSection}>
          {isMaterialsLoading && <Loader fontSize={24} />}
          {!isMaterialsLoading &&
            materialTypes.map((materialType) => {
              const index = values.materials.findIndex(
                (material) => material.materialCode === materialType.code
              );
              const isChecked = index !== -1;
              const quantityKey = `materials[${index}].quantity`;
              const quantity = isChecked
                ? values.materials[index].quantity
                : null;

              return (
                <div className={styles.materialRow} key={materialType.code}>
                  <Checkbox
                    id={`pickup-request-material-${materialType.code}`}
                    value={isChecked}
                    disabled={isSubmitting}
                    title={t(
                      `pickupRequests.materials.codes.${materialType.code}`,
                      { defaultValue: materialType.name }
                    )}
                    onChange={(_event, checked) =>
                      handleMaterialToggle(materialType.code, checked)
                    }
                  />
                  {isChecked && (
                    <TextInput
                      required
                      id={`pickup-request-material-quantity-${materialType.code}`}
                      title={t("pickupRequests.form.quantityLabel")}
                      mask="99999"
                      value={Number.isFinite(quantity) ? String(quantity) : ""}
                      disabled={isSubmitting}
                      isError={!!fieldError(quantityKey)}
                      errorText={fieldError(quantityKey)}
                      wrapperClassName={styles.quantityField}
                      onChange={(_event, newValue) =>
                        updateValue(
                          quantityKey,
                          newValue === "" ? null : Number(newValue),
                          true
                        )
                      }
                      onBlur={() => validateValue(quantityKey)}
                    />
                  )}
                </div>
              );
            })}
          {!isMaterialsLoading && !materialTypes.length && (
            <span className={styles.materialsEmpty}>
              {t("pickupRequests.materials.empty")}
            </span>
          )}
          {!!fieldError("materials") && (
            <span id="materials" className={styles.materialsError}>
              {fieldError("materials")}
            </span>
          )}
          {!!values.materials.length && (
            <div className={styles.materialsTotals}>
              <div className={styles.totalsRow}>
                <span>{t("pickupRequests.form.totalQuantity")}</span>
                <span>{totalQuantity}</span>
              </div>
              <div className={styles.totalsRow}>
                <span>{t("pickupRequests.form.totalWeight")}</span>
                <span>{formatWeight(totalWeight)}</span>
              </div>
            </div>
          )}
        </div>
        {documentsSlot}
      </section>

      <section className={styles.section}>
        {/* The comment field below shares this section, so the heading is gated on its own:
            keeping it would caption an empty section for a collector. */}
        {showAssignment && (
          <h2 className={styles.sectionTitle}>
            {t("pickupRequests.form.haulerSectionTitle")}
          </h2>
        )}
        {showAssignment && (
          <div
            className={styles.formField}
            title={
              isHaulerBlockedByOrder
                ? t("pickupRequests.tooltips.haulerDisabled")
                : undefined
            }
          >
            <AutoComplete
              id="pickup-request-hauler"
              title={t("pickupRequests.form.haulerLabel")}
              placeholder={t("pickupRequests.form.haulerPlaceholder")}
              value={selectedHauler}
              options={haulerAutoCompleteOptions}
              limit={haulerAutoCompleteOptions.length}
              filterOption={filterHaulerOption}
              isEmptyStateVisible
              disabled={isHaulerBlockedByOrder || isSubmitting}
              isError={!!fieldError("haulerId")}
              errorText={fieldError("haulerId")}
              onChange={(_event, newValue) =>
                handleHaulerChange(newValue === null ? CLEAR_SELECTION : newValue)
              }
            />
          </div>
        )}

        {showAssignment && (
          <div
            className={styles.formField}
            title={
              isCarrierBlockedByOrder
                ? t("pickupRequests.tooltips.carrierDisabled")
                : undefined
            }
          >
            <SelectInput
              id="pickup-request-carrier"
              title={t("pickupRequests.form.carrierLabel")}
              placeholder={t("pickupRequests.form.carrierPlaceholder")}
              value={values.carrierId ?? ""}
              options={carrierSelectOptions}
              disabled={
                isCarrierBlockedByOrder || isSubmitting || !carrierOptions.length
              }
              isError={!!fieldError("carrierId")}
              errorText={fieldError("carrierId")}
              onChange={(_event, newValue) =>
                updateValue(
                  "carrierId",
                  newValue === CLEAR_SELECTION ? null : (newValue as number),
                  true
                )
              }
            />
          </div>
        )}

        <TextInput
          isMultiline
          id="pickup-request-comment"
          title={t("pickupRequests.form.commentLabel")}
          value={commentValue}
          disabled={isSubmitting}
          maxLength={MAX_COMMENT_LENGTH}
          isError={!!fieldError("comment")}
          errorText={fieldError("comment")}
          hintText={t("pickupRequests.form.characterCount", {
            count: commentValue.length,
            limit: MAX_COMMENT_LENGTH,
          })}
          onChange={(_event, newValue) =>
            updateValue("comment", newValue, true)
          }
          wrapperClassName={styles.shortField}
        />
      </section>

      {showPortalOnlyFields && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t("pickupRequests.form.prioritySectionTitle")}
          </h2>
          <SelectInput
            id="pickup-request-priority"
            required
            title={t("pickupRequests.form.priorityLabel")}
            placeholder={t("pickupRequests.form.priorityPlaceholder")}
            value={values.priority ?? ""}
            options={priorityOptions}
            disabled={isSubmitting}
            isError={!!fieldError("priority")}
            errorText={fieldError("priority")}
            wrapperClassName={styles.formField}
            onChange={(_event, newValue) =>
              updateValue("priority", newValue as PickupRequestPriorityId, true)
            }
          />

          <TextInput
            isMultiline
            id="pickup-request-priority-reason"
            title={t("pickupRequests.form.priorityReasonLabel")}
            value={priorityReasonValue}
            disabled={isSubmitting}
            maxLength={MAX_PRIORITY_REASON_LENGTH}
            isError={!!fieldError("priorityReason")}
            errorText={fieldError("priorityReason")}
            hintText={t("pickupRequests.form.characterCount", {
              count: priorityReasonValue.length,
              limit: MAX_PRIORITY_REASON_LENGTH,
            })}
            onChange={(_event, newValue) =>
              updateValue("priorityReason", newValue, true)
            }
            wrapperClassName={styles.shortField}
          />
        </section>
      )}

      <div className={styles.actions}>
        {onCancel && (
          <Button
            id="pickup-request-cancel"
            variant="outlined"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {t("pickupRequests.form.cancel")}
          </Button>
        )}
        <div
          title={
            isSubmitDisabled && !isSubmitting
              ? t("pickupRequests.tooltips.submitDisabled")
              : undefined
          }
        >
          <Button
            id="pickup-request-submit"
            variant="filled"
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
          >
            {submitLabel ?? t("pickupRequests.form.submit")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PickupRequestForm;
